import { debounce, mediaQueryLarge } from '@theme/utilities';

const PANEL_SELECTOR = 'homepage-scroll-snap[data-initialized] .shopify-section';

// Caps how many columns get added on top of baseColumns. On very wide viewports a single
// bump can still leave the row tall enough to scroll, so we re-measure and bump again up
// to this limit rather than stopping after one try.
const MAX_COLUMN_BUMP = 2;

// Overflow (px) small enough to be incidental — rounding, collapsed margins — rather than real
// cut-off content. Shared by both fit routines below; see the rationale at its use in
// updateHomepageCarouselFit.
const OVERFLOW_TOLERANCE = 24;

// Narrowing the editorial card can re-wrap its caption text, changing the very height being
// measured — so the fit is re-measured rather than trusted after a single correction.
const MAX_EDITORIAL_FIT_PASSES = 3;

// Never shrink the editorial card below this fraction of its natural width. On a very short
// window it's better to leave a small internal scroll than to render a postage-stamp image.
const MIN_EDITORIAL_CARD_WIDTH_RATIO = 0.6;

/**
 * Homepage scroll-snap panels are capped to the viewport (see base.css), but a
 * product/collection/article carousel's cards can still be tall enough that the panel
 * needs an internal scroll before the next wheel/swipe pages away (see
 * #activePanelCanScroll in homepage-scroll-snap.js). Narrowing the carousel by a column
 * shortens each card (image height is aspect-ratio-driven from its width), which is often
 * enough to make the whole row fit without scrolling — so this bumps the column count up,
 * one column at a time, but only on panels/viewports where it's actually needed.
 */
export function updateHomepageCarouselFit() {
  document.querySelectorAll(PANEL_SELECTOR).forEach((panel) => {
    const carousel = panel.querySelector('.resource-list__carousel');
    if (!(carousel instanceof HTMLElement)) return;

    const baseColumns = Number(carousel.dataset.baseColumns);
    if (!baseColumns) return;

    // Reset first: avoids ratcheting the column count up on repeated resize calls, since
    // the next measurement would otherwise reflect an already-bumped layout.
    carousel.style.setProperty('--column-count', String(baseColumns));

    // --column-count only affects layout at this breakpoint (see the
    // resource-list-carousel container query in base.css); below it, carousels size via
    // --mobile-card-size instead, so there's nothing to measure or bump.
    if (!mediaQueryLarge.matches) return;

    for (let bump = 1; bump <= MAX_COLUMN_BUMP; bump++) {
      // Flat tolerance only (no percentage-of-height scaling, unlike
      // #activePanelCanScroll's tolerance in homepage-scroll-snap.js): this decides
      // whether to shrink the carousel to fit the panel's content, so it should absorb
      // only incidental overflow (rounding, collapsed margins) — not dismiss real
      // cut-off content (e.g. the callout's description/CTA) just because it's a small
      // fraction of a tall viewport.
      const overflow = panel.scrollHeight - panel.clientHeight;
      if (overflow <= OVERFLOW_TOLERANCE) return;

      carousel.style.setProperty('--column-count', String(baseColumns + bump));
    }
  });
}

/**
 * Same problem, different carousel: the editorial-focus carousel (Rudsak Edit) sizes its card from
 * `--editorial-card-width` (min(54.875rem, 61vw) on desktop, 16.5rem on mobile — see
 * blocks/_carousel-content.liquid), and the image inside it is width-driven by its aspect ratio.
 * base.css drops the section's fixed min-heights inside a snap panel, which is enough on a tall
 * window; on a shorter one the card still overflows, so narrow it here — the image, the side peeks
 * (`--editorial-peek`) and the chevron strip (`--editorial-image-height`) are all derived from that
 * one custom property, so they follow automatically.
 */
export function updateEditorialCarouselFit() {
  document.querySelectorAll(PANEL_SELECTOR).forEach((panel) => {
    const carousel = panel.querySelector('.carousel-content--editorial-focus');
    if (!(carousel instanceof HTMLElement)) return;

    // Reset first, for the same reason as the column count above: otherwise each call measures an
    // already-shrunk layout and ratchets the card smaller on every resize.
    carousel.style.removeProperty('--editorial-card-width');

    // Any card will do — they all share `--editorial-card-width` and the same image ratio.
    const card = carousel.querySelector('slideshow-slides > .card');
    const image = card?.querySelector('.image-block__image');
    if (!(card instanceof HTMLElement) || !(image instanceof HTMLElement)) return;

    // offsetWidth/offsetHeight, not getBoundingClientRect(): every card but the focused one carries
    // a `transform: scale(0.92)`, which a rect measurement would fold into the numbers.
    const naturalWidth = card.offsetWidth;
    const imageHeight = image.offsetHeight;
    if (!naturalWidth || !imageHeight) return;

    // The image is the only part of the card that grows with its width, so this converts the
    // height we need to lose into the width to remove.
    const widthPerHeight = naturalWidth / imageHeight;
    const minWidth = naturalWidth * MIN_EDITORIAL_CARD_WIDTH_RATIO;
    let width = naturalWidth;

    for (let pass = 0; pass < MAX_EDITORIAL_FIT_PASSES; pass++) {
      const overflow = panel.scrollHeight - panel.clientHeight;
      if (overflow <= OVERFLOW_TOLERANCE) return;

      width = Math.max(minWidth, width - overflow * widthPerHeight);
      carousel.style.setProperty('--editorial-card-width', `${width}px`);

      if (width <= minWidth) return;
    }
  });
}

function updateCarouselFit() {
  updateHomepageCarouselFit();
  updateEditorialCarouselFit();
}

updateCarouselFit();
window.addEventListener('resize', debounce(updateCarouselFit, 150));

// The editorial card's caption height depends on the web font, which can land after the first
// layout — re-fit once it has, so the initial measurement isn't taken against fallback metrics.
document.fonts?.ready.then(updateEditorialCarouselFit);
